import { useMemo, useState } from "react"
import Seo from "@/components/Seo"
import { Link } from "react-router-dom"
import { MANUFACTURERS, PRODUCTS } from "../data/catalog"
import { QuoteButton } from "@/components/QuoteButton"

export default function Shop() {
  const [search, setSearch] = useState("")
  const [category, setCategory] = useState("All")
  const [condition, setCondition] = useState("All")
  const [brand, setBrand] = useState("")

  const pickBrand = (name) => {
    setBrand((b) => (b === name ? "" : name))
    document.getElementById("product-grid")?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const categories = [
    "All",
    ...new Set(PRODUCTS.map((product) => product.category)),
  ]
  const visibleProducts = useMemo(
    () =>
      PRODUCTS.filter((product) => {
        const matchesSearch = `${product.name} ${product.description}`
          .toLowerCase()
          .includes(search.toLowerCase())
        const matchesCategory =
          category === "All" || product.category === category
        const matchesCondition =
          condition === "All" || product.condition === condition
        const matchesBrand = !brand || product.items.some((item) => item.brand === brand)
        return matchesSearch && matchesCategory && matchesCondition && matchesBrand
      }),
    [category, condition, search, brand],
  )

  return (
    <div>
      <Seo title="Online IT Shop Uganda: Laptops, Networking, CCTV | Jero-One" description="Buy HP, Dell, Lenovo laptops, Cisco, Ubiquiti, Fortinet networking, Hikvision CCTV, APC UPS and Microsoft 365. Pay in USD or UGX; delivery in Uganda." path="/shop" />
      <section className="grid min-h-[58vh] bg-brand-navy lg:grid-cols-[1.1fr_0.9fr]">
        <div className="flex flex-col justify-center px-8 py-20 md:px-16">
          <div className="mb-4 font-mono text-xs tracking-[0.24em] text-brand-green-light">
            JERO-ONE ONLINE IT SHOP
          </div>
          <h1 className="font-display text-6xl font-black leading-none text-brand-light md:text-7xl">
            TECHNOLOGY
            <br />
            <span className="text-brand-green-light">READY TO WORK.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-brand-muted">
            Explore product lines, compare available items, select the right
            specification, and add it to your cart for checkout.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 text-xs font-semibold uppercase tracking-wider text-brand-light">
            <span className="rounded-full border border-brand-green/40 px-4 py-2">
              New equipment
            </span>
            <span className="rounded-full border border-brand-green/40 px-4 py-2">
              Certified refurbished
            </span>
            <span className="rounded-full border border-brand-green/40 px-4 py-2">
              Warranty support
            </span>
          </div>
        </div>
        <div className="relative min-h-80 overflow-hidden">
          <img fetchPriority="high"
            src="/website-assets/images/photo-1654782360205-511dfcdc72ba-1400x1000.jpg"
            alt="Business laptops available from Jero-One"
            className="h-full w-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-transparent to-brand-navy/30" />
          <div className="absolute bottom-8 left-8 right-8 rounded-2xl border border-brand-green/30 bg-brand-navy/90 p-5 backdrop-blur">
            <div className="font-display text-3xl font-black text-brand-light">
              {PRODUCTS.length} PRODUCT LINES
            </div>
            <div className="mt-1 text-sm text-brand-muted">
              Hardware, software, security, cloud, and managed support
            </div>
          </div>
        </div>
      </section>

      <section id="product-grid" className="scroll-mt-28 bg-[#0A0F0B] px-6 py-16 md:px-14">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 grid gap-3 lg:grid-cols-[1fr_auto_auto]">
            <label className="block">
              <span className="sr-only">Search product lines</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search laptops, CCTV, software, support..."
                className="w-full rounded-xl border border-brand-green/20 bg-brand-mid px-5 py-3.5 text-brand-light outline-none transition-colors placeholder:text-brand-muted focus:border-brand-green"
              />
            </label>
            <select
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              aria-label="Filter by category"
              className="rounded-xl border border-brand-green/20 bg-brand-mid px-5 py-3.5 text-sm text-brand-light outline-none"
            >
              {categories.map((item) => (
                <option key={item}>{item}</option>
              ))}
            </select>
            <select
              value={condition}
              onChange={(event) => setCondition(event.target.value)}
              aria-label="Filter by condition"
              className="rounded-xl border border-brand-green/20 bg-brand-mid px-5 py-3.5 text-sm capitalize text-brand-light outline-none"
            >
              {["All", "new", "refurbished"].map((item) => (
                <option key={item}>{item}</option>
              ))}
            </select>
          </div>

          <div className="mb-7 text-sm text-brand-muted">
            Showing{" "}
            <strong className="text-brand-light">
              {visibleProducts.length}
            </strong>{" "}
            product lines
            {brand && (
              <button type="button" onClick={() => setBrand("")} data-testid="brand-filter-chip" className="ml-3 rounded-full border border-brand-green/40 px-3 py-1 text-xs font-semibold text-brand-light transition-colors hover:border-brand-green">
                Brand: {brand} ×
              </button>
            )}
          </div>

          {brand && visibleProducts.length === 0 && (
            <div className="mb-8 flex flex-col items-start gap-4 rounded-2xl border border-brand-green/25 bg-brand-mid p-8 md:flex-row md:items-center md:justify-between" data-testid="brand-empty">
              <div>
                <div className="font-display text-3xl font-black text-brand-light">WE SOURCE {brand.toUpperCase()}</div>
                <p className="mt-2 max-w-xl text-sm text-brand-muted">{brand} products aren't listed online yet, but we supply the full range. Tell us the models and quantities you need and we'll send a quotation.</p>
              </div>
              <QuoteButton items={[{ name: `${brand} products (please specify models in notes)`, quantity: 1 }]} testid="brand-request-quote-btn" />
            </div>
          )}

          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {visibleProducts.map((product) => (
              <Link
                key={product.id}
                to={`/shop/${product.id}`}
                data-testid="shop-line-card"
                className="group overflow-hidden rounded-2xl border border-brand-green/15 bg-brand-mid transition-all hover:-translate-y-1 hover:border-brand-green/50"
              >
                <div className="relative h-56 overflow-hidden bg-brand-navy">
                  <img loading="lazy" decoding="async"
                    src={product.image}
                    alt={product.name}
                    className="h-full w-full object-cover opacity-80 transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-mid to-transparent" />
                  <span className="absolute left-4 top-4 rounded-full bg-brand-navy/90 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-brand-green-light">
                    {product.condition}
                  </span>
                </div>
                <div className="p-6">
                  <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-brand-green-light">
                    {product.category}
                  </div>
                  <h2 className="font-display text-2xl font-bold leading-tight text-brand-light">
                    {product.name}
                  </h2>
                  <p className="mt-3 min-h-16 text-sm leading-relaxed text-brand-muted">
                    {product.description}
                  </p>
                  <div className="mt-5 flex items-center justify-between border-t border-brand-green/10 pt-5">
                    <span className="text-xs text-brand-muted">
                      {product.items.length} items available
                    </span>
                    <span className="text-sm font-bold text-brand-green-light">
                      Select an item →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-brand-green/10 bg-brand-mid px-6 py-16 md:px-14">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-10 lg:grid-cols-[0.7fr_1.3fr]">
            <div>
              <div className="mb-3 font-mono text-xs tracking-widest text-brand-green-light">
                AUTHORIZED SOURCING
              </div>
              <h2 className="font-display text-5xl font-black leading-none text-brand-light">
                LEADING GLOBAL MANUFACTURERS
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-brand-muted">
                Need a model not listed? We procure, configure, deploy, and
                support technology from trusted manufacturers for organizations
                of every size.
              </p>
            </div>
            <div className="flex flex-wrap content-start gap-3" data-testid="manufacturer-logos">
              {MANUFACTURERS.map((manufacturer) => (
                <button
                  type="button"
                  key={manufacturer}
                  title={`Show ${manufacturer} products`}
                  aria-pressed={brand === manufacturer}
                  onClick={() => pickBrand(manufacturer)}
                  data-testid={`brand-${manufacturer.toLowerCase()}`}
                  className={`group flex h-16 w-32 items-center justify-center overflow-hidden rounded-xl border bg-[#0A0F0B] px-4 transition-colors hover:border-brand-green/60 ${brand === manufacturer ? "border-brand-green ring-1 ring-brand-green" : "border-brand-green/20"}`}
                >
                  <img
                    src={`/website-assets/brands/${manufacturer.toLowerCase()}.${["ZKTeco", "VeloCloud"].includes(manufacturer) ? "png" : "svg"}`}
                    alt={manufacturer}
                    loading="lazy"
                    className={`${["Lenovo", "Acer", "Asus", "Cisco"].includes(manufacturer) ? "h-24 w-24 max-w-none" : "max-h-8 max-w-full"} object-contain opacity-75 transition-opacity duration-200 [filter:brightness(0)_invert(1)] group-hover:opacity-100`}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
